import Avatar from '@ui/Avatar';
import Typography from '@ui/Typography';
import React from 'react';
import DonateButton from './DonateButton';
import Link from '@ui/Link';
import AppLink from '../next-ui/Link/Link';
import { cx } from 'cva';
import StripeDonateButton from './StripeDonateButton';
import useUserProfile from '@/hooks/useUserProfile';
import useHasUserSession from '@/hooks/useHasUserSession';
import FollowNonProfitButton from '../Buttons/FollowNonProfitButton';
import { abbreviateNumber } from '@/utils/stringHelpers';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import PostType from '@/constants/postType.constants';
import { useUpdateNonProfitFollowers } from '@/actions/feed.actions';
import { isDonateDisabled } from '@/utils/donate.helper';
import { isValidPostId } from '@/utils/post.helper';

function NonProfitProfileBar(props) {
  const { nonProfit, post = null } = props;

  const { mutate: updateNonProfitFollowers } = useUpdateNonProfitFollowers();
  const hasUserSession = useHasUserSession();
  const user = useUserProfile();
  const isLesserThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );

  const isUserPost = PostType.UserPost === post?.sourcePostType;
  const withDonate =
    !!post &&
    isUserPost &&
    !isDonateDisabled(user, nonProfit) &&
    isValidPostId(post?.id);

  if (nonProfit.blocked) {
    return null;
  }

  let recordDonationClickPayload = {
    type: 'post',
    postId: post?.id,
    donationClick: {
      userId: user?.id,
      activeCommunityId: user?.activeCommunity?.id,
      location: user?.location,
    },
  };

  const handleToggleFollowNonProfit = (isFollowing) => {
    updateNonProfitFollowers({
      nonProfitId: nonProfit.id,
      offset: isFollowing ? 1 : -1,
    });
  };

  const renderDonateButton = () => {
    return (
      <StripeDonateButton
        // variant="outlined"
        nonProfit={nonProfit}
        post={post}
        withIcon
        className="flex-shrink-0 flex-grow-0"
        recordPayload={post?.id ? recordDonationClickPayload : null}
      />
    );
  };

  return (
    <div className="flex w-full items-center justify-between gap-2 bg-primary-light-1 px-4 py-4 lg:px-6">
      <div className="flex items-center gap-2">
        <Avatar src={nonProfit.logo_url} alt={nonProfit.name} size="md" />
        <div className="flex flex-col">
          <Typography
            variant="body1"
            color="textDisabled"
            className="text-16 font-semibold">
            {'Beneficiary'}
          </Typography>
          <div className="flex items-center">
            <div className="flex items-center gap-2">
              <AppLink href={nonProfit.slug ? `/${nonProfit.slug}` : null}>
                <Typography
                  variant="h5"
                  // component="a"
                  // href={nonProfitUrl}
                  color={nonProfit.slug ? 'primary' : 'textSecondary'}
                  className={cx(
                    'w-fit text-16 font-semibold group-hover:text-text-hyperlink',
                    // isLesserThanMediumScreen ? 'text-14' : 'text-20',
                  )}>
                  {nonProfit.name}
                </Typography>
              </AppLink>
              {hasUserSession && !isLesserThanMediumScreen && (
                <FollowNonProfitButton
                  size="sm"
                  nonProfit={nonProfit}
                  // noConfirmation
                  className="ml-2 text-primary"
                  onToggle={handleToggleFollowNonProfit}
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {nonProfit?.numFollowers > 0 && (
              <Typography variant="body2" color="textSecondary">
                {abbreviateNumber(nonProfit.numFollowers)} follower
                {nonProfit.numFollowers > 1 ? 's' : ''}
              </Typography>
            )}
            {hasUserSession && isLesserThanMediumScreen && (
              <FollowNonProfitButton
                size="sm"
                nonProfit={nonProfit}
                // noConfirmation
                className="ml-2 text-primary"
                onToggle={handleToggleFollowNonProfit}
              />
            )}
          </div>
        </div>
      </div>
      {withDonate && renderDonateButton()}
    </div>
  );
}

export default NonProfitProfileBar;
